import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import AuthenticationApiSerivce from "../../../../api/AuthServices";
import { resolveUserSubmitErrorMessage } from "../../../../api/submitRequestErrors";
import { RegistrationLayout } from "../../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../../RegistrationLayout/RegistrationSetupNavbar";
import RegistrationSetupWrapper from "../../RegistrationSetup.style";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../../helper/constants";
import {
  getRegistrationData,
  updateRegistrationData,
} from "../../registrationSetupStorage";
import { markCoverageReturnToInsuranceVerification } from "../../registrationNavigation";
import {
  redirectIfProviderUnavailableBeforeApi,
  redirectIfProviderUnavailableFromResponse,
} from "../../registrationRdAvailability";
import {
  trackRegistrationError,
  trackRegistrationInsuranceVerified,
  trackRegistrationStepCompleted,
} from "../../../../helper/registrationAnalytics";
import {
  buildRecheckInsuranceEstimateFormData,
  buildVerifyEligibilityFormData,
  getInsuranceVerificationFailureMessage,
  isEligibilityVerified,
  parseVerifyEligibilityResponse,
} from "../insuranceProviders";
import { getInsuranceCardImages } from "../insuranceCardImagesStore";
import {
  buildCoverageResultFromEligibility,
  DISCOVERY_CHECK_STEP_KEYS,
  DISCOVERY_INITIAL_COPY_KEYS,
  DISCOVERY_RECHECK_COPY_KEYS,
  DISCOVERY_STEP_DELAYS,
} from "../registrationInsuranceMock";

const CHECKING_STEP = REGISTRATION_SETUP_STEP_META.INSURANCE_CHECKING;
const RECHECK_STEP = REGISTRATION_SETUP_STEP_META.INSURANCE_CHECKING_RECHECK;

const StepCheckIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 12 12"
    fill="none"
    stroke="#fff"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="2 6 5 9 10 3" />
  </svg>
);

const getStepClassName = (index, activeStep, doneSteps) => {
  if (doneSteps.includes(index)) return "discovery-step done";
  if (activeStep === index) return "discovery-step active";
  return "discovery-step";
};

const RegistrationInsuranceChecking = ({ history }) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(-1);
  const [doneSteps, setDoneSteps] = useState([]);
  const [isRecheck, setIsRecheck] = useState(false);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevRootHeight = root?.style.height ?? "";

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    if (root) {
      root.style.height = "100vh";
      root.style.height = "100dvh";
    }

    const data = getRegistrationData();
    if (!data.firstName && !data.email) {
      history.replace("/register/account/payment");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }

    const recheck = Boolean(data.insuranceRecheck);
    const verification = data.insuranceVerification;
    const manual = data.insuranceManual;

    if (recheck) {
      if (!manual?.provider || !manual?.memberId) {
        history.replace("/register/account/insurance/details");
        return () => {
          html.style.overflow = prevHtmlOverflow;
          body.style.overflow = prevBodyOverflow;
          if (root) root.style.height = prevRootHeight;
        };
      }
    } else if (
      !verification?.memberId ||
      (!verification?.payer && !verification?.providerName)
    ) {
      history.replace("/register/account/insurance");
      return () => {
        html.style.overflow = prevHtmlOverflow;
        body.style.overflow = prevBodyOverflow;
        if (root) root.style.height = prevRootHeight;
      };
    }

    setIsRecheck(recheck);

    let cancelled = false;
    const timeouts = [];

    DISCOVERY_STEP_DELAYS.forEach((delay, index) => {
      timeouts.push(
        window.setTimeout(() => {
          if (cancelled) return;
          setActiveStep(index);
          if (index > 0) {
            setDoneSteps((prev) =>
              prev.includes(index - 1) ? prev : [...prev, index - 1]
            );
          }
        }, delay)
      );
    });

    const runEligibilityAndNavigate = async () => {
      try {
        const latestData = getRegistrationData();

        if (redirectIfProviderUnavailableBeforeApi(history, latestData)) {
          return;
        }

        let payload;

        if (recheck) {
          payload = buildRecheckInsuranceEstimateFormData(
            manual,
            latestData,
            getInsuranceCardImages()
          );
        } else {
          payload = buildVerifyEligibilityFormData(
            latestData.insuranceVerification,
            latestData,
            getInsuranceCardImages()
          );
        }

        if (!payload) {
          throw new Error(
            recheck
              ? "NUTRITION_INSDISC_ERR_BUILD_REQUEST_RECHECK"
              : "NUTRITION_INSDISC_ERR_BUILD_REQUEST"
          );
        }

        const res = recheck
          ? await AuthenticationApiSerivce.recheckInsuranceEstimate(
              payload,
              i18n.language
            )
          : await AuthenticationApiSerivce.verifyInsuranceEligibility(
              payload,
              i18n.language
            );

        if (cancelled) return;

        if (redirectIfProviderUnavailableFromResponse(history, res)) {
          return;
        }

        const parsed = parseVerifyEligibilityResponse(res);
        if (!parsed.ok) {
          throw new Error(
            parsed.errorMessage || "NUTRITION_INSDISC_ERR_VERIFY_COVERAGE"
          );
        }

        const eligibilityResult = parsed.eligibilityResult;

        if (
          redirectIfProviderUnavailableFromResponse(history, {
            data: eligibilityResult,
          })
        ) {
          return;
        }

        const dataAfterCheck = getRegistrationData();
        updateRegistrationData({
          insuranceEligibilityResult: eligibilityResult,
        });

        if (isEligibilityVerified(eligibilityResult)) {
          const coverageResult = buildCoverageResultFromEligibility(
            eligibilityResult,
            dataAfterCheck.insuranceVerification,
            recheck ? manual : undefined,
            dataAfterCheck.insuranceProviderOptions
          );
          if (!recheck) {
            markCoverageReturnToInsuranceVerification();
          }
          updateRegistrationData({
            coverageResult,
            insuranceRecheck: false,
            insuranceVerificationFailureMessage: null,
          });
          trackRegistrationInsuranceVerified({
            method: recheck ? "manual" : "auto",
            result: "verified",
          });
          trackRegistrationStepCompleted("insurance", 3);
          history.replace("/register/account/insurance/coverage");
          return;
        }

        const failureMessage = getInsuranceVerificationFailureMessage(
          res,
          eligibilityResult
        );
        updateRegistrationData({
          insuranceRecheck: false,
          insuranceVerificationFailureMessage: failureMessage || null,
        });
        trackRegistrationInsuranceVerified({
          method: recheck ? "manual" : "auto",
          result: "fallback",
        });
        history.replace("/register/account/insurance/details");
      } catch (err) {
        if (cancelled) return;
        trackRegistrationInsuranceVerified({
          method: recheck ? "manual" : "auto",
          result: "failed",
        });
        trackRegistrationError({
          stepName: "insurance",
          stepNumber: 3,
          errorType: "api_failed",
        });
        message.error(
          resolveUserSubmitErrorMessage(err, {
            fallback: t("NUTRITION_INSDISC_ERR_VERIFY_COVERAGE"),
            translate: t,
          })
        );
        history.replace(
          recheck
            ? "/register/account/insurance/details"
            : "/register/account/insurance"
        );
      }
    };

    runEligibilityAndNavigate();

    return () => {
      cancelled = true;
      timeouts.forEach((id) => window.clearTimeout(id));
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      if (root) {
        root.style.height = prevRootHeight;
      }
    };
  }, [history, t]);

  const copyKeys = isRecheck
    ? DISCOVERY_RECHECK_COPY_KEYS
    : DISCOVERY_INITIAL_COPY_KEYS;
  const stepMeta = isRecheck ? RECHECK_STEP : CHECKING_STEP;

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={stepMeta}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            showBack={false}
          />
        }
      >
        <div className="scroll-body scroll-body-centered">
          <div className="content-card content-card-centered">
            <div className="discovery-checking">
              <div className="discovery-spinner-wrap">
                <div className="discovery-spinner" aria-hidden />
                <div className="discovery-spinner-icon" aria-hidden>
                  🔍
                </div>
              </div>
              <div className="discovery-checking-title">{t(copyKeys.titleKey)}</div>
              <div className="discovery-checking-sub">{t(copyKeys.subtitleKey)}</div>

              <div className="discovery-steps">
                {DISCOVERY_CHECK_STEP_KEYS.map((stepKey, index) => (
                  <div
                    key={stepKey}
                    className={getStepClassName(index, activeStep, doneSteps)}
                  >
                    <div className="discovery-step-dot">
                      {doneSteps.includes(index) ? (
                        <StepCheckIcon />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <span>{t(stepKey)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </RegistrationLayout>
    </RegistrationSetupWrapper>
  );
};

export default withRouter(RegistrationInsuranceChecking);
